import React, { useEffect, useState } from "react";
import { cn } from "~/lib/cn";
import { ButtonLink } from "./button";

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountdownBarProps {
  /** The text to display before the countdown (e.g., "Summit starts in:") */
  text: string;
  /** The target date for the countdown in ISO string format */
  targetDate: string;
  /** The CTA button text */
  ctaText: string;
  /** The CTA button link URL */
  ctaLink: string;
  /** Optional CSS class name for additional styling */
  className?: string;
  /** Whether to show the countdown (defaults to true) */
  showCountdown?: boolean;
  /** The variant of the CTA button */
  ctaVariant?: "default" | "secondary" | "fancy" | "outline" | "ghost";
  /** The size of the CTA button */
  ctaSize?: "default" | "sm" | "lg";
  /** Attributes that could have been passed by Builder.io */
  attributes?: Record<string, string>;
}

function calculateTimeLeft(targetDate: string): CountdownTime {
  const endDate = new Date(targetDate);
  const now = new Date();
  const difference = endDate.getTime() - now.getTime();

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
}

export const CountdownBar = ({
  text,
  targetDate,
  ctaText,
  ctaLink,
  className,
  showCountdown = true,
  ctaVariant = "fancy",
  ctaSize = "default",
  attributes,
}: CountdownBarProps) => {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>(() =>
    calculateTimeLeft(targetDate),
  );

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div
      {...attributes}
      className={cn(
        attributes?.className,
        "sticky! top-0 w-full left-0 right-0 z-50 bg-white border-b border-t border-gray-200 shadow-sm",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-screen-xl px-2 py-4 lg:px-4">
        <div className="flex flex-col items-center justify-between gap-x-8 gap-y-4 sm:flex-row">
          <div className="flex flex-1 items-center gap-x-4">
            <div className="flex flex-col">
              <p className="text-sm leading-6 text-text-primary">
                <span className="font-medium">{text}</span>
              </p>
              {showCountdown && (
                <div className="mt-1 flex items-center gap-3">
                  <div className="grid grid-flow-col gap-2 text-center text-sm">
                    <div className="flex flex-col">
                      <span className="font-mono font-medium text-lg">
                        {timeLeft.days.toString().padStart(2, "0")}
                      </span>
                      <span className="text-xs text-gray-500">DAYS</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="font-mono font-medium text-lg">
                        {timeLeft.hours.toString().padStart(2, "0")}
                      </span>
                      <span className="text-xs text-gray-500">HOURS</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="font-mono font-medium text-lg">
                        {timeLeft.minutes.toString().padStart(2, "0")}
                      </span>
                      <span className="text-xs text-gray-500">MINS</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="font-mono font-medium text-lg">
                        {timeLeft.seconds.toString().padStart(2, "0")}
                      </span>
                      <span className="text-xs text-gray-500">SECS</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center">
            <ButtonLink
              href={ctaLink}
              variant={ctaVariant}
              size={ctaSize}
              className="whitespace-nowrap"
            >
              {ctaText}
            </ButtonLink>
          </div>
        </div>
      </div>
    </div>
  );
};
